import React, { useState } from 'react'
import {
  Box, Typography, IconButton, Tooltip,
  CircularProgress, Chip,
} from '@mui/material'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import CreateNewFolderOutlinedIcon from '@mui/icons-material/CreateNewFolderOutlined'
import SelectAllIcon from '@mui/icons-material/SelectAll'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { FancyButton } from './FancyButtons'
import { FS_SM, ICON_SM, ICON_MD } from '../common/theme'
import type { ExtractedQuestion } from '../types'
import SectionedQuestionList, {
  type FlatItem, type SectionMeta,
} from './SectionedQuestionList'

// Re-export shared list types so BidImportQuestionsModal can still
// import { type FlatItem, type SectionMeta } from this file as before.
export type { FlatItem, SectionMeta }

let _sectionCounter = 0
function newSectionId() { return `biqsec${++_sectionCounter}` }

interface BiqOrganizePhaseProps {
  items: FlatItem[]
  setItems: React.Dispatch<React.SetStateAction<FlatItem[]>>
  sections: SectionMeta[]
  setSections: React.Dispatch<React.SetStateAction<SectionMeta[]>>
  setQuestions: React.Dispatch<React.SetStateAction<ExtractedQuestion[]>>
  qMap: Record<string, ExtractedQuestion>
  acceptedCount: number
  isUploading: boolean
  onBack: () => void
  onCancel: () => void
  onImport: () => void
}

// Phase 3 of the bid-import wizard. Wraps the shared
// SectionedQuestionList with a toolbar (Add Section, Select All,
// bulk-remove) and bottom buttons (Back / Cancel / Import).
export default function BiqOrganizePhase({
  items, setItems, sections, setSections, setQuestions, qMap,
  acceptedCount, isUploading, onBack, onCancel, onImport,
}: BiqOrganizePhaseProps) {
  // Bulk-select for organize-phase operations. Separate from the
  // `question.selected` flag the review phase uses for merging.
  const [organizeSelected, setOrganizeSelected] = useState<Set<string>>(new Set())

  // Only include-flagged questions participate in the organize-phase list.
  const isQuestionVisible = (q: ExtractedQuestion) => !!q.include

  const allIncludedShas = items
    .filter(i => i.type === 'question' && qMap[(i as Extract<FlatItem, { type: 'question' }>).sha]?.include)
    .map(i => (i as Extract<FlatItem, { type: 'question' }>).sha)
  const allSelected = allIncludedShas.length > 0 && allIncludedShas.every(s => organizeSelected.has(s))

  const toggleSelect = (sha: string) => {
    setOrganizeSelected(prev => {
      const next = new Set(prev)
      if (next.has(sha)) next.delete(sha); else next.add(sha)
      return next
    })
  }
  const toggleSelectAll = () => {
    setOrganizeSelected(allSelected ? new Set() : new Set(allIncludedShas))
  }

  const addSection = () => {
    const id = newSectionId()
    const name = `Section ${sections.length + 1}`
    setSections(prev => [...prev, { id, name }])
    setItems(prev => [{ type: 'section', sectionId: id }, ...prev])
  }

  const deleteSection = (id: string) => {
    setSections(prev => prev.filter(s => s.id !== id))
    setItems(prev => prev.filter(i => !(i.type === 'section' && i.sectionId === id)))
  }

  const bulkDelete = () => {
    if (organizeSelected.size === 0) return
    const shas = organizeSelected
    setQuestions(prev => prev.filter(q => !shas.has(q.questionsha)))
    setItems(prev => prev.filter(it => !(it.type === 'question' && shas.has(it.sha))))
    setOrganizeSelected(new Set())
  }

  const handleNumberingCommit = (sha: string, numbering: string) => {
    setQuestions(prev => prev.map(q =>
      q.questionsha === sha ? { ...q, numbering: numbering || undefined } : q
    ))
  }

  return (
    <Box sx={{ display: 'flex', flex: 1, minHeight: 0, flexDirection: 'column' }}>
      {/* Toolbar - add section, select-all, bulk delete chip */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.5, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
        <Tooltip title="Add Section">
          <IconButton data-testid="biq_org_addsection_btn" size="small" onClick={addSection} sx={{ p: 0.25 }}>
            <CreateNewFolderOutlinedIcon sx={{ fontSize: ICON_MD }} />
          </IconButton>
        </Tooltip>
        <Tooltip title={allSelected ? 'Deselect all' : 'Select all questions'}>
          <IconButton
            data-testid="biq_org_selectall_btn"
            size="small"
            onClick={toggleSelectAll}
            sx={{
              p: 0.25, borderRadius: 1,
              bgcolor: allSelected ? 'primary.main' : 'transparent',
              color: allSelected ? '#fff' : 'inherit',
              '&:hover': { bgcolor: allSelected ? 'primary.dark' : 'action.hover' },
            }}
          >
            <SelectAllIcon sx={{ fontSize: ICON_MD }} />
          </IconButton>
        </Tooltip>
        {organizeSelected.size > 0 && (
          <>
            <Chip
              data-testid="biq_org_selected_chip"
              size="small"
              label={`${organizeSelected.size} selected`}
              color="primary"
              onDelete={() => setOrganizeSelected(new Set())}
              sx={{ fontSize: '0.7rem', height: 20 }}
            />
            <Tooltip title="Remove selected questions">
              <IconButton
                data-testid="biq_org_bulkdelete_btn"
                size="small"
                color="error"
                onClick={bulkDelete}
                sx={{ p: 0.25 }}
              >
                <DeleteOutlineIcon sx={{ fontSize: ICON_MD }} />
              </IconButton>
            </Tooltip>
          </>
        )}
        <Box sx={{ flex: 1 }} />
        <Typography data-testid="biq_org_count" variant="caption" sx={{ fontSize: FS_SM, opacity: 0.7 }}>
          {acceptedCount} {acceptedCount === 1 ? 'question' : 'questions'}
        </Typography>
      </Box>

      {/* List - shared component handles sections + questions + drag/drop */}
      <Box data-testid="biq_org_list" sx={{ flex: 1, overflowY: 'auto' }}>
        <SectionedQuestionList<ExtractedQuestion>
          testIdPrefix="biq_org"
          items={items}
          setItems={setItems}
          sections={sections}
          setSections={setSections}
          qMap={qMap}
          selectedShas={organizeSelected}
          onToggleSelect={toggleSelect}
          isQuestionVisible={isQuestionVisible}
          onNumberingCommit={handleNumberingCommit}
          onDeleteSection={deleteSection}
          groupDragEnabled
        />
      </Box>

      {/* Bottom buttons - Back / Cancel / Import */}
      <Box sx={{ display: 'flex', gap: 1, p: 1, borderTop: 1, borderColor: 'divider', flexShrink: 0 }}>
        <FancyButton
          testId="biq_org_back_btn"
          onClick={onBack}
          disabled={isUploading}
        >
          <ArrowBackIcon sx={{ fontSize: ICON_SM, mr: 0.5 }} />
          Back
        </FancyButton>
        <Box sx={{ flex: 1 }} />
        <FancyButton testId="biq_org_cancel_btn" onClick={onCancel} disabled={isUploading}>Cancel</FancyButton>
        <FancyButton
          testId="biq_org_import_btn"
          solid
          onClick={onImport}
          disabled={isUploading || acceptedCount === 0}
        >
          {isUploading ? <CircularProgress size={16} /> : `Import ${acceptedCount}`}
        </FancyButton>
      </Box>
    </Box>
  )
}
